'use client';

import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { toast } from 'sonner';
import { logErrorDetails } from '@/lib/utils';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
  useEffect(() => {
    // Log the error details for debugging
    logErrorDetails(error, 'PageError');
    
    // Show a toast notification
    toast.error('Page Error', {
      description: 'An error occurred while loading this page.',
    });
  }, [error]);

  const handleReset = () => {
    reset();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <CardTitle className="text-xl font-semibold text-gray-900">
            Page Error
          </CardTitle>
          <CardDescription className="text-gray-600">
            Something went wrong while loading this page.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Show error details in development */}
          {process.env.NODE_ENV === 'development' && (
            <div className="rounded-md bg-gray-100 p-3 text-sm">
              <p className="font-medium text-gray-900">Error Details (Development):</p>
              <p className="text-red-600 mt-1">{error.message}</p>
              {error.digest && (
                <p className="text-gray-600 mt-1">
                  <span className="font-medium">Error ID:</span> {error.digest}
                </p>
              )}
              {error.stack && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-gray-700">Stack trace</summary>
                  <pre className="mt-2 text-xs text-gray-600 whitespace-pre-wrap overflow-auto max-h-48">
                    {error.stack}
                  </pre>
                </details>
              )}
            </div>
          )}
          
          {/* Show error digest in production for debugging */}
          {process.env.NODE_ENV === 'production' && error.digest && (
            <div className="rounded-md bg-blue-50 p-3 text-sm">
              <p className="text-blue-800">
                <span className="font-medium">Error ID:</span> {error.digest}
              </p>
              <p className="text-blue-600 text-xs mt-1">
                Please provide this ID when reporting the issue.
              </p>
            </div>
          )}
          
          <div className="flex flex-col space-y-2">
            <Button 
              onClick={handleReset}
              className="w-full"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
            <Button 
              variant="outline" 
              onClick={handleGoHome}
              className="w-full"
            >
              <Home className="mr-2 h-4 w-4" />
              Go Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 